import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Calendar, Clock, Monitor, MapPin, ExternalLink, Users, FileText, Bell, Info, Lock } from 'lucide-react';
import EventModal from './EventModal';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import './EventViewModal.css';

const TYPE_COLORS = {
  'HR Screening Call':           { bg: '#EFF6FF', text: '#1D4ED8', dot: '#3B82F6' },
  'HR Interview':                { bg: '#EEF2FF', text: '#4338CA', dot: '#6366F1' },
  'Technical Interview':         { bg: '#F0FDF4', text: '#15803D', dot: '#22C55E' },
  'Technical Exam / Assessment': { bg: '#FFFBEB', text: '#B45309', dot: '#F59E0B' },
  'Final Interview':             { bg: '#FDF4FF', text: '#7E22CE', dot: '#A855F7' },
  'Offer Discussion':            { bg: '#ECFDF5', text: '#065F46', dot: '#10B981' },
  'Background Check':            { bg: '#F0F9FF', text: '#0369A1', dot: '#0EA5E9' },
  'Follow-up Call':              { bg: '#FFF7ED', text: '#C2410C', dot: '#F97316' },
  'Networking Call':             { bg: '#F0FAFE', text: '#0E7490', dot: '#06B6D4' },
  'Other':                       { bg: '#F9FAFB', text: '#4B5563', dot: '#9CA3AF' },
};

const InfoRow = ({ icon, label, value, href }) => {
  if (!value) return null;
  return (
    <div className="eview-row">
      <span className="eview-row-icon">{icon}</span>
      <div className="eview-row-body">
        <span className="eview-row-label">{label}</span>
        {href
          ? <a href={href} target="_blank" rel="noreferrer" className="eview-row-link">{value}</a>
          : <span className="eview-row-value">{value}</span>}
      </div>
    </div>
  );
};

const getEventColors = (type, customTypes = []) => {
  if (TYPE_COLORS[type]) return TYPE_COLORS[type];
  const ct = customTypes.find(c => c.label === type);
  if (ct) return { bg: ct.color + '20', text: ct.color, dot: ct.color };
  return TYPE_COLORS['Other'];
};

const EventViewModal = ({ event, jobs, customTypes = [], onCustomTypesChange, onClose, onUpdate, onDelete, onPrepare, onUpgrade, onReminderToggle }) => {
  const { user } = useAuth();
  const isPro = user?.tier === 'pro';
  const [editMode, setEditMode] = useState(false);
  const [remindOn, setRemindOn] = useState(event?.remindByEmail || false);
  const [togglingRemind, setTogglingRemind] = useState(false);

  useEffect(() => {
    if (!editMode) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
    return undefined;
  }, [editMode]);

  const toggleReminder = async () => {
    if (!isPro) { onUpgrade?.(); return; }
    setTogglingRemind(true);
    const newVal = !remindOn;
    try {
      await api.put(`/api/events/${event._id}`, { remindByEmail: newVal, reminderDayBefore: true, reminderDayOf: true });
      setRemindOn(newVal);
      onReminderToggle?.({ ...event, remindByEmail: newVal, reminderDayBefore: true, reminderDayOf: true });
      toast.success(newVal ? 'Reminders turned on' : 'Reminders off');
    } catch {
      toast.error('Failed to update reminder');
    } finally { setTogglingRemind(false); }
  };

  if (editMode) {
    return (
      <EventModal
        event={event}
        jobs={jobs}
        customTypes={customTypes}
        onCustomTypesChange={onCustomTypesChange}
        onClose={() => setEditMode(false)}
        onAdd={null}
        onUpdate={async (id, data) => { await onUpdate(id, data); setEditMode(false); }}
        onDelete={onDelete}
      />
    );
  }

  const colors = getEventColors(event.type, customTypes);
  const linkedJob = jobs?.find(j => j._id === (event.job?._id || event.job));
  const hasLimitedInfo = !linkedJob && !event.notes;

  const fmtDate = (d) => {
    try { return format(new Date(d), "EEEE, MMMM d, yyyy 'at' h:mm a"); } catch { return '—'; }
  };
  const fmtEnd = (d) => {
    try { return format(new Date(d), 'h:mm a'); } catch { return null; }
  };

  return (
    <div className="eview-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="eview-panel">

        {/* Header */}
        <div className="eview-header" style={{ background: colors.bg }}>
          <div className="eview-type-dot" style={{ background: colors.dot }} />
          <div className="eview-header-body">
            <div className="eview-type-label" style={{ color: colors.text }}>{event.type}</div>
            <h2 className="eview-title">{event.title}</h2>
            {linkedJob && (
              <div className="eview-job-tag">
                <span className="eview-job-avatar" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
                  {linkedJob.company[0]}
                </span>
                {linkedJob.company} — {linkedJob.role}
              </div>
            )}
          </div>
          <button className="eview-close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="eview-body">

          {/* Gentle nudge if info is sparse */}
          {hasLimitedInfo && (
            <div className="eview-nudge">
              <span><Info size={14} /></span>
              <span>
                This event isn't linked to a job or doesn't have notes — the AI preparation guide will be more general.
                <button className="eview-nudge-edit" onClick={() => setEditMode(true)}>Add details →</button>
              </span>
            </div>
          )}

          {/* Event details */}
          <div className="eview-details">
            <InfoRow icon={<Calendar size={14} />} label="Date & Time" value={fmtDate(event.date)} />
            {event.endDate && fmtEnd(event.endDate) && (
              <InfoRow icon={<Clock size={14} />} label="Ends" value={fmtEnd(event.endDate)} />
            )}

            {event.isVirtual
              ? <InfoRow icon={<Monitor size={14} />} label="Format" value="Virtual / Online" />
              : <InfoRow icon={<MapPin size={14} />} label="Venue" value={event.venue || 'Face-to-face (venue not set)'} />
            }

            {event.meetingLink && (
              <InfoRow icon={<ExternalLink size={14} />} label="Meeting Link" value="Join Meeting ↗" href={event.meetingLink} />
            )}

            {event.contacts?.filter(c => c.name || c.email).length > 0 && (
              <div className="eview-contacts">
                <div className="eview-section-label">
                  <Users size={14} /> Contacts
                </div>
                {event.contacts.filter(c => c.name || c.email).map((c, i) => (
                  <div key={i} className="eview-contact-item">
                    <div className="eview-contact-name">{c.name || '—'}{c.role ? ` · ${c.role}` : ''}</div>
                    {c.email && <a href={`mailto:${c.email}`} className="eview-contact-link">{c.email}</a>}
                    {c.phone && <span className="eview-contact-phone">{c.phone}</span>}
                  </div>
                ))}
              </div>
            )}

            {event.notes && (
              <div className="eview-notes">
                <div className="eview-section-label"><FileText size={14} /> Notes</div>
                <div className="eview-notes-text">{event.notes}</div>
              </div>
            )}

            {event.remindByEmail && (
              <div className="eview-reminder-badge">
                <Bell size={13} /> Email reminder set
                {event.reminderDayBefore && ' · Day before'}
                {event.reminderDayOf && ' · Day of'}
              </div>
            )}
          </div>
        </div>

        {/* Action footer */}
        <div className="eview-footer">
          <button className="eview-btn-delete" onClick={() => onDelete(event._id)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
              <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
            </svg>
            Delete
          </button>
          <div className="eview-footer-right">
            {/* Reminder toggle */}
            <button
              className={`eview-btn-remind ${!isPro ? 'remind-locked' : remindOn ? 'remind-on' : 'remind-off'}`}
              onClick={toggleReminder}
              disabled={togglingRemind}
              title={!isPro ? 'Email reminders — Pro feature' : remindOn ? 'Email reminders are ON — click to turn off' : 'Turn on email reminders'}
            >
              {isPro ? (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 01-3.46 0"/>
                  {!remindOn && <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2"/>}
                </svg>
              ) : (
                <Lock size={13} />
              )}
              {isPro ? (remindOn ? 'Reminders On' : 'Remind Me') : 'Remind Me'}
              {!isPro && <span className="eview-remind-pro-badge">Pro</span>}
            </button>
            <button className="eview-btn-prepare" onClick={() => onPrepare(event)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
              </svg>
              Prepare
            </button>
            <button className="eview-btn-edit" onClick={() => setEditMode(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              Edit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventViewModal;
